import { clamp } from './math';
import Click from './Click';
import GestureList from './GestureList';
import ZoomControlElementsBuilder from './ZoomControlElementsBuilder';

const noop = () => { };

interface ZoomControlCallbacks {
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  onReset?: () => void;
}

type ZoomSettings = {
  x: number;
  y: number;
  scale: number;
};

function areSettingsEqual(a: ZoomSettings, b: ZoomSettings) {
  return a.x === b.x && a.y === b.y && a.scale === b.scale;
}

class ViewportZoomControl {
  onZoomIn!: () => void;

  onZoomOut!: () => void;

  onReset!: () => void;

  btnZoomInEl: HTMLButtonElement;

  btnZoomOutEl: HTMLButtonElement;

  btnResetEl: HTMLButtonElement;

  $el: HTMLDivElement;

  gestures: GestureList | undefined;

  currentZoomSettings: ZoomSettings = { x: 0, y: 0, scale: 0 };

  centeredZoomSettings: ZoomSettings = { x: 0, y: 0, scale: 0 };

  isBtnZoomInEnabled = true;

  isBtnZoomOutEnabled = true;

  isBtnResetEnabled = true;

  constructor(rootHeadingLevel: number) {
    const elements = ZoomControlElementsBuilder.build(rootHeadingLevel);
    this.$el = elements.el;

    this.btnZoomInEl = elements.btnZoomInEl;
    this.btnZoomOutEl = elements.btnZoomOutEl;
    this.btnResetEl = elements.btnResetEl;

    this.gestures = new GestureList([
      new Click(this.btnResetEl, (event: Event) => {
        event.preventDefault();
        this.reset();
      }),
      new Click(this.btnZoomInEl, (event: Event) => {
        event.preventDefault();
        this.zoomIn();
      }),
      new Click(this.btnZoomOutEl, (event: Event) => {
        event.preventDefault();
        this.zoomOut();
      }),
    ]);
  }

  setCallbacks = ({
    onZoomIn,
    onZoomOut,
    onReset,
  }: ZoomControlCallbacks = {}) => {
    this.onZoomIn = onZoomIn || noop;
    this.onZoomOut = onZoomOut || noop;
    this.onReset = onReset || noop;
  };

  startListening() {
    this.gestures?.start();
  }

  stopListening() {
    this.gestures?.stop();
  }

  setCurrentZoomSettings({ x, y, scale }: ZoomSettings) {
    const value = {
      x,
      y,
      scale: clamp(scale, 0, 1),
    };

    if (areSettingsEqual(value, this.currentZoomSettings)) {
      return;
    }

    this.currentZoomSettings = value;

    this.updateDisplay();
  }

  setCenteredZoomSettings(value: ZoomSettings) {
    this.centeredZoomSettings = value;
  }

  reset = () => {
    this.onReset();
  };

  updateDisplay() {
    window.requestAnimationFrame(() => {
      const { currentZoomSettings, centeredZoomSettings } = this;

      if (currentZoomSettings.scale === 1) {
        if (this.isBtnZoomInEnabled === true) {
          disableButton(this.btnZoomInEl);
          this.isBtnZoomInEnabled = false;
        }
      } else {
        if (this.isBtnZoomInEnabled === false) {
          enableButton(this.btnZoomInEl);
          this.isBtnZoomInEnabled = true;
        }
      }

      if (currentZoomSettings.scale === 0) {
        if (this.isBtnZoomOutEnabled === true) {
          disableButton(this.btnZoomOutEl);
          this.isBtnZoomOutEnabled = false;
        }
      } else {
        if (this.isBtnZoomOutEnabled === false) {
          enableButton(this.btnZoomOutEl);
          this.isBtnZoomOutEnabled = true;
        }
      }

      const isCentered = areSettingsEqual(
        currentZoomSettings,
        centeredZoomSettings,
      );

      if (isCentered) {
        if (this.isBtnResetEnabled === true) {
          disableButton(this.btnResetEl);
          this.isBtnResetEnabled = false;
        }
      } else {
        if (this.isBtnResetEnabled === false) {
          enableButton(this.btnResetEl);
          this.isBtnResetEnabled = true;
        }
      }
    });
  }

  zoomIn = () => {
    this.onZoomIn();
  };

  zoomOut = () => {
    this.onZoomOut();
  };

  destroy() {
    this.stopListening();
    
    this.gestures?.destroy();
    
    this.gestures = undefined;

    this.onZoomIn = noop;
    this.onZoomOut = noop;
    this.onReset = noop;
  }
}

function enableButton($btn: HTMLButtonElement) {
  $btn.removeAttribute('aria-disabled');
  $btn.classList.remove('disabled');
}

function disableButton($btn: HTMLButtonElement) {
  $btn.setAttribute('aria-disabled', 'true');
  $btn.classList.add('disabled');
}

export default ViewportZoomControl;
